import type { PdfSeitenMass } from './pdf-dokument'

/** CSS-Pixel je PDF-Punkt bei 100 %: 96 Pixel je Zoll auf dem Schirm gegen 72 Punkte je Zoll. */
export const PUNKT_ZU_PX = 96 / 72

/** Die Stufen hinter Plus, Minus und dem Zoom-Menue, in Prozent der Originalgroesse. */
export const ZOOM_STUFEN = [25, 50, 75, 100, 125, 150, 200, 300, 400] as const
export const ZOOM_MIN = ZOOM_STUFEN[0]
export const ZOOM_MAX = ZOOM_STUFEN[ZOOM_STUFEN.length - 1]

/** Innenrand der Flaeche um die Seiten, in Pixeln. Steht genauso im `padding` von `.seiten`. */
export const RAND = 12

/**
 * Untergrenze fuer den Start („automatisch"): darunter wird 10-pt-Fliesstext kleiner als 10 px. Die
 * erste Fassung stand bei 60 %, und am Laptop war damit auf einem Abholnachweis nichts zu erkennen;
 * von Hand gezoomt passten 77 % (Tim 2026-10-25). Auf einem Laptop oder dem iPad quer passt eine
 * A4-Seite im Dialog erst bei 50 bis 55 %, dort wird also ein Stueck gescrollt.
 */
export const AUTO_MIN_PROZENT = 75

export type PdfZoom = { art: 'automatisch' } | { art: 'seitenbreite' } | { art: 'ganzeSeite' } | { art: 'prozent'; wert: number }

export interface Flaeche {
  readonly breite: number
  readonly hoehe: number
}

/** Die Seite so, wie sie nach einer Drehung um `drehung` Grad dasteht. */
export function gedreht(seite: PdfSeitenMass, drehung: number): PdfSeitenMass {
  return drehung % 180 === 0 ? seite : { breite: seite.hoehe, hoehe: seite.breite }
}

/**
 * CSS-Pixel je PDF-Punkt fuer eine Zoomstufe. „Seitenbreite" passt die breiteste Seite in die
 * Flaeche, „Ganze Seite" die erste Seite ganz. „Automatisch" (der Start) zeigt die ganze Seite, aber
 * nie kleiner als `AUTO_MIN_PROZENT` und nie breiter als die Flaeche. Ohne gemessene Flaeche gilt 100 %.
 */
export function skalaFuer(zoom: PdfZoom, seiten: readonly PdfSeitenMass[], drehung: number, flaeche: Flaeche): number {
  if (zoom.art === 'prozent') {
    return (begrenzeProzent(zoom.wert) / 100) * PUNKT_ZU_PX
  }
  const breite = flaeche.breite - 2 * RAND
  if (seiten.length === 0 || breite <= 0) {
    return PUNKT_ZU_PX
  }
  const breitesteSeite = Math.max(...seiten.map((s) => gedreht(s, drehung).breite))
  const nachBreite = breite / breitesteSeite
  if (zoom.art === 'seitenbreite') {
    return Math.max(0.05, nachBreite)
  }
  const erste = gedreht(seiten[0], drehung)
  const hoehe = flaeche.hoehe - 2 * RAND
  const nachHoehe = hoehe > 0 ? hoehe / erste.hoehe : nachBreite
  const ganzeSeite = Math.max(0.05, Math.min(breite / erste.breite, nachHoehe))
  if (zoom.art === 'ganzeSeite') {
    return ganzeSeite
  }
  const lesbar = (AUTO_MIN_PROZENT / 100) * PUNKT_ZU_PX
  return Math.max(0.05, Math.min(nachBreite, Math.max(ganzeSeite, lesbar)))
}

export function prozentVon(skala: number): number {
  return (skala / PUNKT_ZU_PX) * 100
}

export function begrenzeProzent(prozent: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, prozent))
}

/** Die naechste Stufe ueber (`1`) oder unter (`-1`) dem jetzigen Wert, am Rand der Rand selbst. */
export function naechsteStufe(prozent: number, richtung: 1 | -1): number {
  if (richtung === 1) {
    return ZOOM_STUFEN.find((stufe) => stufe > prozent * 1.001) ?? ZOOM_MAX
  }
  return [...ZOOM_STUFEN].reverse().find((stufe) => stufe < prozent * 0.999) ?? ZOOM_MIN
}

export function zoomBeschriftung(zoom: PdfZoom, prozent: number): string {
  switch (zoom.art) {
    case 'seitenbreite':
      return 'Seitenbreite'
    case 'ganzeSeite':
      return 'Ganze Seite'
    case 'automatisch':
    case 'prozent':
      return `${Math.round(prozent)} %`
  }
}

/** Faktor fuer einen Schritt am Mausrad mit Strg, gedeckelt, damit ein Ruck nicht ueber drei Stufen springt. */
export function radZoomFaktor(deltaY: number, deltaMode: number): number {
  const pixel = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY
  return Math.exp(-Math.max(-200, Math.min(200, pixel)) * 0.002)
}

/**
 * Die Seite (ab 1), die gerade gelesen wird: die letzte, deren Oberkante oberhalb von 40 % der
 * sichtbaren Hoehe liegt.
 */
export function seiteAnPosition(oberkanten: readonly number[], scrollTop: number, hoehe: number): number {
  const marke = scrollTop + hoehe * 0.4
  const oberhalb = oberkanten.filter((oben) => oben <= marke).length
  return Math.max(1, oberhalb)
}

/**
 * Wie viele Geraetepixel je CSS-Pixel die Leinwand bekommt. Auf einem Retina-Schirm das
 * Pixelverhaeltnis, aber gedeckelt: Safari zeichnet eine Leinwand ueber rund 16 Millionen Pixel
 * gar nicht, und bei 400 % waere eine A4-Seite dort sonst leer.
 */
export function ausgabeSkalierung(breite: number, hoehe: number, pixelVerhaeltnis: number, maxPixel = 16_000_000): number {
  const gewuenscht = pixelVerhaeltnis > 0 ? pixelVerhaeltnis : 1
  const flaeche = breite * hoehe
  if (flaeche <= 0 || flaeche * gewuenscht * gewuenscht <= maxPixel) {
    return gewuenscht
  }
  return Math.sqrt(maxPixel / flaeche)
}

/**
 * Ob die Seiten (in CSS-Pixeln) samt Rand und Abstand ueber die Flaeche hinausragen. Nur dann laesst
 * sich das Blatt mit der Maus verschieben, und nur dann zeigt der Zeiger die Hand.
 */
export function ragtUeber(seiten: readonly Flaeche[], flaeche: Flaeche): boolean {
  if (seiten.length === 0 || flaeche.breite <= 0 || flaeche.hoehe <= 0) {
    return false
  }
  const breite = Math.max(...seiten.map((s) => s.breite)) + 2 * RAND
  const hoehe = seiten.reduce((summe, s) => summe + s.hoehe, 0) + (seiten.length + 1) * RAND
  return breite > flaeche.breite + 1 || hoehe > flaeche.hoehe + 1
}

/** `aspect-ratio` der Seite, so wie sie nach der Drehung dasteht. */
export function seitenverhaeltnis(seite: PdfSeitenMass, drehung: number): string {
  const s = gedreht(seite, drehung)
  return `${Math.round(s.breite)} / ${Math.round(s.hoehe)}`
}

/** Dateiname zum Herunterladen: ohne Zeichen, die Windows nicht erlaubt, und mit `.pdf` am Ende. */
export function pdfDateiname(name: string | undefined): string {
  const sauber = (name ?? '').replace(/[\\/:*?"<>|]+/g, '-').trim()
  const basis = sauber || 'Dokument'
  return /\.pdf$/i.test(basis) ? basis : `${basis}.pdf`
}

/**
 * Ob ein Blob als PDF gezeigt werden soll. Ein Blob ohne Typ oder als `octet-stream` wird versucht
 * (pdf.js erkennt ein PDF am Inhalt), ein Bild oder Text nicht.
 */
export function alsPdfVersuchen(blob: Blob): boolean {
  return ['application/pdf', '', 'application/octet-stream', 'binary/octet-stream'].includes(blob.type)
}
